import {
  Badge,
  Button,
  Chip,
  DataTable,
  Drawer,
  IconButton,
  Stack,
  styled,
  TextField,
  Typography,
  useSnackbar,
  VirtualizedAutocomplete,
} from '@chronosphereio/chrono-ui';
import {
  CheckboxBlankOutline as CheckboxBlankOutlineIcon,
  CheckboxMarked as CheckboxMarkedIcon,
} from 'mdi-material-ui';
import { useCallback, useMemo, useState } from 'react';
import { VariableInjectionType } from '@/core';
import { BULK_INJECT_EVENTS } from '@/dashboards/utils/bulk-inject-analytics';
import { useAnalytics } from '@/plugin-system';
import { usePluginCapabilities } from '@/plugin-system/runtime/DataQueryPlugins';
import {
  useBulkInjectVariable,
  useDashboardMetadata,
  useDashboardStore,
  useListPanelsWithMetadata,
  usePanelLabelExtraction,
} from '../../context/DashboardProvider';
import { isValidLabelName, useTemplateVariableDefinitions } from '../../context/TemplateVariableProvider';
import { DEFAULT_COLUMN_PROPS, DEFAULT_TABLE_PROPS } from '../../utils/tables';

interface PanelRow {
  panelKey: string;
  panelName: string;
  panelType: string;
  groupTitle?: string;
  alreadyUsesVariable: boolean;
  supportsInjection: boolean;
  labelNames: string[];
}

interface BulkManageVariableDialogProps {
  open: boolean;
  variableName: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function BulkManageVariableDialog({ open, variableName, onClose, onSuccess }: BulkManageVariableDialogProps) {
  const [selectedPanelKeys, setSelectedPanelKeys] = useState<Set<string>>(new Set());
  const [labelName, setLabelName] = useState('');
  const [labelNameTouched, setLabelNameTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const analytics = useAnalytics();
  const { metadata } = useDashboardMetadata();
  const { errorSnackbar } = useSnackbar();
  const panelsMetadata = useListPanelsWithMetadata({ includeVariableUsage: true });
  const { injectVariable } = useBulkInjectVariable();
  const panels = useDashboardStore((state) => state.panels);
  const variableDefinitions = useTemplateVariableDefinitions();

  // Determine variable type and injection mode
  const variableDefinition = useMemo(
    () => variableDefinitions.find((v) => v.spec.name === variableName),
    [variableDefinitions, variableName]
  );
  const isIntervalVariable = variableDefinition?.kind === 'IntervalVariable';
  const injectionType: VariableInjectionType = isIntervalVariable ? 'RangeVector' : 'LabelMatcher';

  // Load plugins and check if variable injection is supported
  const { pluginSupportsInjection, isLoadingPlugins, initializedPlugins } = usePluginCapabilities(panels);

  // Extract label names for panels that use the variable
  const { panelLabelNames, isLoading: isLoadingLabels } = usePanelLabelExtraction({
    open,
    isLoadingPlugins,
    panelsMetadata,
    panels,
    variableName,
    initializedPlugins,
  });

  // Build table rows for each panel with variable usage info
  const rows: PanelRow[] = useMemo(() => {
    return panelsMetadata.map((panel) => {
      const alreadyUsesVariable = panel.variableUsage?.includes(variableName) ?? false;

      // Check if panel supports injection based on queries used
      const panelDef = panels[panel.panelKey];
      const supportsInjection =
        panelDef?.spec.queries?.some((queryDef) => {
          const pluginKind = queryDef.spec?.plugin?.kind;
          return pluginKind && pluginSupportsInjection.get(pluginKind) === true;
        }) ?? false;

      // Extract relevant label names
      const labelNames = panelLabelNames.get(panel.panelKey) ?? [];

      return {
        panelKey: panel.panelKey,
        panelName: panel.panelName,
        panelType: panel.panelType,
        groupTitle: panel.groupTitle,
        alreadyUsesVariable,
        supportsInjection,
        labelNames,
      };
    });
  }, [panelsMetadata, variableName, panels, pluginSupportsInjection, panelLabelNames]);

  // Build list of suggested label names for the autocomplete
  const suggestedLabelNames = useMemo(() => {
    const suggestions = new Set<string>();

    // Add label names from panels that already use this variable
    rows.forEach((row) => {
      if (row.alreadyUsesVariable) {
        row.labelNames.forEach((name) => suggestions.add(name));
      }
    });

    // Only show suggestions if we found any from existing panels
    // Otherwise return empty array to allow free-form input
    return Array.from(suggestions);
  }, [rows]);

  const handleTogglePanel = useCallback((panelKey: string) => {
    setSelectedPanelKeys((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(panelKey)) {
        newSet.delete(panelKey);
      } else {
        newSet.add(panelKey);
      }
      return newSet;
    });
  }, []);

  const handleApply = useCallback(async () => {
    // For LabelMatcher injection, require a valid label name
    // For RangeVector injection, label name is not needed
    if (selectedPanelKeys.size === 0) return;
    if (injectionType === 'LabelMatcher' && !isValidLabelName(labelName)) return;

    setIsLoading(true);

    try {
      const injectionResult = await injectVariable(
        injectionType === 'RangeVector'
          ? {
              variableName,
              panelKeys: Array.from(selectedPanelKeys),
              injectionType: 'RangeVector',
            }
          : {
              variableName,
              labelName,
              panelKeys: Array.from(selectedPanelKeys),
              injectionType: 'LabelMatcher',
            }
      );

      const successCount = injectionResult.success.length;
      const failedCount = injectionResult.failed.length;

      analytics?.track(BULK_INJECT_EVENTS.APPLIED, {
        dashboardSlug: metadata.slug,
        variableName,
        injectionType,
        labelName: injectionType === 'LabelMatcher' ? labelName : undefined,
        panelCount: selectedPanelKeys.size,
        successCount,
        failedCount,
      });

      // Show error notification if there are failed injections.
      // Success notification is handled by the undo notification system (applyBulkVariableInjection pushToHistory).
      if (failedCount > 0) {
        errorSnackbar(
          `Failed to inject into ${failedCount} panel${failedCount !== 1 ? 's' : ''}: ${injectionResult.failed.map((f) => f.panelName).join(', ')}`
        );
      }

      // Notify the parent on any successful injections. The parent decides
      // whether success should close the dialog or a larger surface.
      if (successCount > 0) {
        onSuccess?.();
      }
    } catch (error) {
      errorSnackbar(error instanceof Error ? error.message : 'Failed to inject variable');
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedPanelKeys,
    variableName,
    labelName,
    injectionType,
    injectVariable,
    errorSnackbar,
    onSuccess,
    analytics,
    metadata.slug,
  ]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Validate input conditions
  const isLabelNameValid = isValidLabelName(labelName);
  const hasPanelsSelected = selectedPanelKeys.size > 0;
  const hasValidInput = isIntervalVariable || isLabelNameValid;
  const isNotBusy = !isLoading && !isLoadingPlugins;
  const canApply = hasPanelsSelected && hasValidInput && isNotBusy;

  // Label name help text based on input state
  const showLabelNameError = !isIntervalVariable && labelNameTouched && !isLabelNameValid;
  let labelNameHelperText: string;
  if (showLabelNameError) {
    labelNameHelperText =
      'Label name must start with a letter or underscore, followed by letters, digits, or underscores';
  } else if (labelName) {
    labelNameHelperText = `The label matcher will be: ${labelName}=~"$${variableName}"`;
  } else {
    labelNameHelperText = 'Select the Prometheus label to match against this variable';
  }

  // Build columns with panel selection, name, type, group, and status
  const columns = useMemo(
    () => [
      {
        ...DEFAULT_COLUMN_PROPS,
        field: 'selected',
        headerName: '',
        width: 60,
        renderCell: ({ row }: { row: PanelRow }) => {
          if (row.alreadyUsesVariable || !row.supportsInjection) {
            return null;
          }
          const isSelected = selectedPanelKeys.has(row.panelKey);
          return (
            <IconButton
              data-testid="toggle-panel-checkbox"
              size="small"
              aria-label={isSelected ? `Deselect panel ${row.panelName}` : `Select panel ${row.panelName}`}
              aria-pressed={isSelected}
              onClick={(e) => {
                e.stopPropagation();
                handleTogglePanel(row.panelKey);
              }}
            >
              {isSelected ? <CheckboxMarkedIcon /> : <CheckboxBlankOutlineIcon />}
            </IconButton>
          );
        },
      },
      {
        ...DEFAULT_COLUMN_PROPS,
        field: 'panelName',
        headerName: 'Panel Name',
        flex: 2,
      },
      {
        ...DEFAULT_COLUMN_PROPS,
        field: 'panelType',
        headerName: 'Type',
        flex: 1,
      },
      {
        ...DEFAULT_COLUMN_PROPS,
        field: 'groupTitle',
        headerName: 'Group',
        flex: 1,
      },
      {
        ...DEFAULT_COLUMN_PROPS,
        field: 'status',
        headerName: 'Status',
        flex: 2,
        renderCell: ({ row }: { row: PanelRow }) => {
          if (isLoadingPlugins) {
            return <VariableStatusBadge size="small" variant="default" label="Checking..." />;
          }
          if (!row.supportsInjection) {
            return <VariableStatusBadge size="small" variant="default" label="Not supported" />;
          }
          if (row.alreadyUsesVariable) {
            if (isLoadingLabels) {
              return <VariableStatusBadge size="small" variant="info" label="Already uses variable" />;
            }
            if (row.labelNames.length > 0) {
              const tooltipText = `Label${row.labelNames.length > 1 ? 's' : ''}: ${row.labelNames.join(', ')}`;
              return (
                <VariableStatusBadge
                  size="small"
                  variant="info"
                  label="Already uses variable"
                  TooltipContent={tooltipText}
                />
              );
            }
            return <VariableStatusBadge size="small" variant="info" label="Already uses variable" />;
          }
          return null;
        },
      },
    ],
    [selectedPanelKeys, handleTogglePanel, isLoadingPlugins, isLoadingLabels]
  );

  return (
    <Drawer open={open} onClose={handleClose} data-testid="bulk-inject-variable-dialog">
      <Drawer.Header
        title={`Manage variable: $${variableName}`}
        primaryButtonText="Apply"
        primaryButtonDisabled={!canApply}
        onPrimaryButtonClick={handleApply}
        onSecondaryButtonClick={handleClose}
      />
      <Drawer.Content>
        <Stack spacing={3}>
          {isIntervalVariable ? (
            <Typography variant="body2" color="text.legacySecondary">
              {`This interval variable will be injected into range vector durations (e.g., [5m] becomes [$${variableName}])`}
            </Typography>
          ) : (
            <VirtualizedAutocomplete
              freeSolo
              multiple={false}
              options={suggestedLabelNames}
              value={labelName}
              onChange={(_event, newValue) => {
                setLabelName(newValue ?? '');
                setLabelNameTouched(true);
              }}
              onInputChange={(_event, newInputValue) => {
                setLabelName(newInputValue);
              }}
              onBlur={() => setLabelNameTouched(true)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Label name"
                  placeholder="Select or type a label name"
                  error={showLabelNameError}
                  helperText={labelNameHelperText}
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  {option}
                </li>
              )}
              size="small"
            />
          )}
          <div>
            <Stack direction="row" spacing={2} mb={2} alignItems="center">
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  const selectablePanelKeys = rows
                    .filter((row) => !row.alreadyUsesVariable && row.supportsInjection)
                    .map((row) => row.panelKey);
                  setSelectedPanelKeys(new Set(selectablePanelKeys));
                }}
              >
                Select all
              </Button>
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  setSelectedPanelKeys(new Set());
                }}
              >
                Select none
              </Button>
              <Chip label={`${selectedPanelKeys.size} selected`} />
            </Stack>
            <DataTable
              {...DEFAULT_TABLE_PROPS}
              getRowId={(row) => row.panelKey}
              rows={rows}
              columns={columns}
              autoHeight
              hideFooter
              localeText={{
                noRowsLabel: 'No panels in dashboard',
              }}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
              }}
              onRowClick={(params) => {
                if (!params.row.alreadyUsesVariable && params.row.supportsInjection) {
                  handleTogglePanel(params.row.panelKey);
                }
              }}
            />
          </div>
        </Stack>
      </Drawer.Content>
    </Drawer>
  );
}

const VariableStatusBadge = styled(Badge)({
  alignSelf: 'center',
});
